import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Input, Flex, Icon, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, useColorModeValue } from "@chakra-ui/react";
import { MainTable } from "../../../components/Table/MainTable";
import { useAuth0 } from "@auth0/auth0-react";
import { useEmiSummary } from "../../../lib/hooks";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { SidebarContext } from "../../../contexts/SidebarContext";
import FilterSystem, { getActiveFilterCount, getFilterDisplayValue, removeFilter as removeFilterHelper } from "../../../components/filters/FilterSystem";

function EmiSummary() {
  const { user, isLoading: userLoading } = useAuth0();
  const { sidebarCollapsed } = useContext(SidebarContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);

  useEffect(() => {
    const token = localStorage.getItem("auth0_token");
    if (user && !userLoading && token) setMounted(true);
  }, [user, userLoading]);

  const { emiSummary, isEmiSummaryLoading } = useEmiSummary(mounted);
  const allRows = Array.isArray(emiSummary?.body) ? emiSummary.body : Array.isArray(emiSummary) ? emiSummary : [];

  const accessors = [
    "invoiceNo",
    "invoiceDate",
    "totalEmis",
    "emisPaid",
    "totalAmountPaid",
    "totalAmountOverdue",
    "remainingToPay",
    "totalOverdueDays",
    "knoxStatus",
  ];
  const [filters, setFilters] = useState({});
  const [sortKey, setSortKey] = useState("invoiceDate");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  const knoxStatuses = useMemo(
    () => Array.from(new Set((allRows||[]).map((r)=>r.knoxStatus).filter(Boolean))).sort(),
    [allRows]
  );
  const totalEmisOptions = useMemo(
    () => Array.from(new Set((allRows||[]).map((r)=>r.totalEmis).filter((x)=>x!==null && x!==undefined))).sort((a,b)=>a-b),
    [allRows]
  );

  const filterFields = useMemo(
    () => [
      {
        key: "knoxStatus",
        label: "Knox Status",
        type: "multiselect",
        options: knoxStatuses,
      },
      {
        key: "totalEmis",
        label: "Total EMIs",
        type: "multiselect",
        options: totalEmisOptions.map((t) => ({ value: String(t), label: `${t} EMIs` })),
      },
      {
        key: "invoiceNo",
        label: "Invoice Number",
        type: "text",
        placeholder: "Enter invoice number",
      },
      {
        key: "invoiceDate",
        label: "Invoice Date",
        type: "daterange",
      },
      {
        key: "totalOverdueDays",
        label: "Min Overdue Days",
        type: "number",
        placeholder: "Minimum overdue days",
      },
    ],
    [knoxStatuses, totalEmisOptions]
  );

  const filtered = useMemo(() => {
    const toStr = (v) => (v === null || v === undefined ? "" : String(v)).toLowerCase();
    return allRows.filter((r) => {
      // Knox status filter (multiselect)
      if (filters.knoxStatus && Array.isArray(filters.knoxStatus) && filters.knoxStatus.length > 0) {
        if (!filters.knoxStatus.includes(r.knoxStatus)) {
          return false;
        }
      }

      // Total EMIs filter (multiselect)
      if (filters.totalEmis && Array.isArray(filters.totalEmis) && filters.totalEmis.length > 0) {
        if (!filters.totalEmis.includes(String(r.totalEmis))) {
          return false;
        }
      }

      // Invoice number filter
      if (filters.invoiceNo && !toStr(r.invoiceNo).includes(toStr(filters.invoiceNo))) {
        return false;
      }

      // Invoice date range filter
      if (filters.invoiceDate_from || filters.invoiceDate_to) {
        const invDate = r.invoiceDate;
        if (invDate) {
          if (filters.invoiceDate_from && invDate < filters.invoiceDate_from) {
            return false;
          }
          if (filters.invoiceDate_to && invDate > filters.invoiceDate_to) {
            return false;
          }
        }
      }

      // Min overdue days filter
      if (filters.totalOverdueDays) {
        const minDays = Number(filters.totalOverdueDays);
        if (!isNaN(minDays) && (r.totalOverdueDays || 0) < minDays) {
          return false;
        }
      }

      // Search filter
      if (search) {
        const hay = accessors.map((a) => toStr(r[a])).join(" ");
        if (!hay.includes(search.toLowerCase())) return false;
      }

      return true;
    });
  }, [allRows, filters, search, accessors]);

  const sortedAll = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      const na = typeof va === "number" ? va : isNaN(Number(va)) ? null : Number(va);
      const nb = typeof vb === "number" ? vb : isNaN(Number(vb)) ? null : Number(vb);
      let cmp;
      if (na !== null && nb !== null) cmp = na - nb;
      else cmp = String(va ?? "").localeCompare(String(vb ?? ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalCount = sortedAll.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const pageSlice = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedAll.slice(start, start + perPage);
  }, [sortedAll, page, perPage]);

  const handleHeaderSort = (accessor) => {
    if (sortKey === accessor) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(accessor);
      setSortDir("asc");
    }
  };

  const columns = useMemo(
    () => [
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("invoiceNo")} cursor="pointer">Invoice No {sortKey==="invoiceNo" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "invoiceNo" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("invoiceDate")} cursor="pointer">Invoice Date {sortKey==="invoiceDate" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "invoiceDate" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("totalEmis")} cursor="pointer">Total EMIs {sortKey==="totalEmis" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "totalEmis" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("emisPaid")} cursor="pointer">EMIs Paid {sortKey==="emisPaid" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "emisPaid" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("totalAmountPaid")} cursor="pointer">Total Paid {sortKey==="totalAmountPaid" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "totalAmountPaid" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("totalAmountOverdue")} cursor="pointer">Total Overdue {sortKey==="totalAmountOverdue" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "totalAmountOverdue" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("remainingToPay")} cursor="pointer">Remaining {sortKey==="remainingToPay" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "remainingToPay" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("totalOverdueDays")} cursor="pointer">Overdue Days {sortKey==="totalOverdueDays" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "totalOverdueDays" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("knoxStatus")} cursor="pointer">Knox Status {sortKey==="knoxStatus" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "knoxStatus" },
    ],
    [sortKey, sortDir]
  );

  const data = useMemo(() => {
    return pageSlice.map((r) => ({
      invoiceNo: r.invoiceNo ?? "-",
      invoiceDate: r.invoiceDate ?? "-",
      totalEmis: r.totalEmis ?? 0,
      emisPaid: r.emisPaid ?? 0,
      totalAmountPaid: r.totalAmountPaid ?? 0,
      totalAmountOverdue: r.totalAmountOverdue ?? 0,
      remainingToPay: r.remainingToPay ?? 0,
      totalOverdueDays: r.totalOverdueDays ?? 0,
      knoxStatus: r.knoxStatus ?? "-",
    }));
  }, [pageSlice]);

  const exportRows = useMemo(() => {
    return sortedAll.map((r) => ({
      invoiceNo: r.invoiceNo ?? "",
      invoiceDate: r.invoiceDate ?? "",
      totalEmis: r.totalEmis ?? "",
      emisPaid: r.emisPaid ?? "",
      totalAmountPaid: r.totalAmountPaid ?? "",
      totalAmountOverdue: r.totalAmountOverdue ?? "",
      remainingToPay: r.remainingToPay ?? "",
      totalOverdueDays: r.totalOverdueDays ?? "",
      knoxStatus: r.knoxStatus ?? "",
    }));
  }, [sortedAll]);

  return (
    <Box
      position="fixed"
      top="80px"
      left={{ base: 0, xl: sidebarCollapsed ? "80px" : "290px" }}
      right="0"
      bottom="0"
      display="flex"
      flexDirection="column"
      p={{ base: "20px", md: "30px" }}
      pe="20px"
      overflow="hidden"
    >
      <Box mb={3} flexShrink={0}>
        <Flex gap={3} alignItems="center" mb={3}>
          <Input
            placeholder="Search all columns"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            flex="1"
          />
          <FilterSystem
            filterFields={filterFields}
            onFilterChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
            initialFilters={filters}
            externalFilters={filters}
            showTags={false}
          />
        </Flex>
        {getActiveFilterCount(filters) > 0 && (
          <Wrap spacing={2}>
            {filterFields.map((field) => {
              if (field.type === "daterange") {
                const fromValue = filters[`${field.key}_from`];
                const toValue = filters[`${field.key}_to`];
                if (fromValue || toValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {fromValue || "..."} to {toValue || "..."}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(`${field.key}_from`, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              } else {
                const value = filters[field.key];
                const hasValue = field.type === "multiselect"
                  ? Array.isArray(value) && value.length > 0
                  : value;

                if (hasValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {getFilterDisplayValue(field, value)}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(field.key, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              }
              return null;
            })}
          </Wrap>
        )}
      </Box>
      <Box flex="1" minH="0">
        <MainTable
          loaded={!isEmiSummaryLoading}
          data={data}
          columns={columns}
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={page}
          perPage={perPage}
          perPageOptions={[50, 100, 200]}
          exports={exportRows}
          totalPage={totalPages}
          name={`EMI Summary (${totalCount})`}
          containerHeight="100%"
        />
      </Box>
    </Box>
  );
}

export default EmiSummary;
